<template>
  <div
    data-test-id="item-modifier-set"
  >
    <div
      class="q-mb-sm relative-position"
      :class="errorMessage ? 'text-red' : undefined"
    >
      <div class="text-h5">
        {{ itemModifierSet.modifierSet.name }}
      </div>
      <div>
        <template v-if="itemModifierSet.minSelectionCount">
          <b>Required</b> <span class="q-mx-xs"> &middot;</span>
        </template>
        <template v-if="errorMessage">
          <q-icon
            name="error"
            color="red"
            size="xs"
          /> {{ errorMessage }}
        </template>
        <template v-else>
          {{ itemModifierSet.choiceCountDescription }}
        </template>
      </div>
    </div>

    <div
      class="row"
      :class="$q.screen.gt.xs ? 'q-col-gutter-lg' : 'q-col-gutter-sm'"
    >
      <div
        v-for="modifier in modifiersComputed"
        :key="modifier.id"
        :class="$q.screen.gt.xs ? 'col-6' : 'col-12'"
      >
        <q-card
          flat
          bordered
          class="position-relative"
          :class="{
            'bg-responsive-grey-2': itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id),
            'text-red': errorMessage
          }"
          @click="handleClick(modifier)"
        >
          <q-card-section
            horizontal
            class="justify-between items-center q-pa-sm"
            :style="reduceOpacity({ item, modifier }) ? 'opacity: 0.3;' : ''"
          >
            <q-icon
              :name="itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id) ? 'fas fa-square-check' : 'fal fa-square'"
              :color="itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id) ? 'primary' : errorMessage ? 'red' : 'responsive'"
              :class="{ 'bg-white': itemOrderItem.hasModifier(modifier, itemModifierSet.modifierSet.id) }"
              style="height: 20px;"
              class="col-auto q-mr-sm"
              size="md"
            />
            <q-img
              v-if="modifier.image"
              :ratio="1"
              class="rounded-borders q-mr-sm"
              :src="modifier.imageUrl"
              :style="{
                width: $q.screen.lt.sm? '20px' : '30px',
                height: $q.screen.lt.sm? '20px' : '30px',
                'background-color': modifier.color
              }"
            />
            <div
              class="col"
            >
              <div
                :class="$q.screen.lt.sm ? 'text-h6' : 'text-h5'"
                style="line-height: 1.5;"
              >
                {{ modifier.name }}
              </div>

              <div
                v-if="modifier.description"
                class="text-caption"
                style="line-height: 1;"
              >
                {{ modifier.description }}
              </div>
            </div>

            <slot
              name="price"
              :modifier="modifier"
            >
              <div
                v-if="modifier.price"
                class="col-auto"
                style="min-width: 60px;"
              >
                <i18n-n
                  :value="modifier.price"
                  tag="span"
                  class="text-subtitle1"
                  format="currency"
                  scope="global"
                />
              </div>
            </slot>
          </q-card-section>
          <q-badge
            v-if="showAvailabilityBadge({ item, modifier })"
            class="absolute-top-right"
            :color="availabilityBadgeColor({ item, modifier })"
            :label="availableStock({ item, modifier })"
          />
        </q-card>
      </div>
    </div>
  </div>
</template>

<script>
import { useFormChild } from 'quasar'
import SalesRestrictionsMixin from 'src/modules/mixins/SalesRestrictionsMixin'
import { nextTick, ref, toRefs, watchEffect } from 'vue'

export default {
  name: 'ItemModifierSet',
  mixins: [SalesRestrictionsMixin],
  configuration: ['currentSite'],
  context: ['currentUser'],
  props: {
    orderItem: {
      type: Object,
      required: true
    },
    itemOrderItem: {
      type: Object,
      required: true
    },
    itemModifierSet: {
      type: Object,
      requred: true,
      default: null
    },
    availableOnline: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  setup (props) {
    const { itemOrderItem, itemModifierSet } = toRefs(props)

    const validating = ref(false)
    const errorMessage = ref(null)

    watchEffect(() => {
      if (validating.value) {
        errorMessage.value = itemOrderItem.value.validateForModifierSet(itemModifierSet.value)
      } else {
        errorMessage.value = null
      }
    })

    async function validate () {
      validating.value = true
      await nextTick()
      return !errorMessage.value
    }

    useFormChild({ validate })

    return { errorMessage }
  },
  data () {
    return {
      validating: false
    }
  },
  computed: {
    inventoryStrategy () {
      return this.currentSite.activeInventoryStrategy
    },
    item () {
      return this.itemOrderItem.item
    },
    modifiersComputed () {
      const modifiers = this.availableOnline
        ? this.itemModifierSet?.modifierSet?.onlineModifiers || []
        : this.itemModifierSet?.modifierSet?.modifiers?.data || []

      return _.filter(modifiers, modifier => {
        if (!modifier.active) return false

        if (this.$register) return true

        if (this.inventoryStrategy?.excludeOutOfStockItemsForKioskAndConsumer && this.isOutOfStock({ item: this.item, modifier })) {
          return false
        }

        return !modifier.salesDisabled
      })
    }
  },
  methods: {
    toggleModifier (modifier) {
      this.itemOrderItem.toggleModifier(modifier, this.itemModifierSet.modifierSetId)
    },
    handleClick (modifier) {
      if (this.itemOrderItem.hasModifier(modifier, this.itemModifierSet.modifierSetId)) {
        this.toggleModifier(modifier, this.itemModifierSet.modifierSetId)
        return
      }

      this.checkForSalesRestrictions({
        item: this.item,
        modifier,
        successCallback: (adminApproved = false) => {
          this.itemOrderItem.adminApproved = adminApproved
          this.toggleModifier(modifier, this.itemModifierSet.modifierSetId)
        }
      })
    }
  }
}

</script>
